import PropTypes from 'prop-types';

import { trans } from '@spotahome/soyuz/client';

import { addMonths } from 'date-fns';

import {
  getDownPaymentForFirstMonthStrategy,
  getPaymentsForFirstMonthStrategy,
  getDownPaymentText
} from '../utils/pricing';

import { formatMonthlyPaymentDate, getDateAsUTC } from '../utils/dateUtils';

import PaymentInfoComponent from './PaymentInfoComponent';
import { transLL } from './utils';

const SAH_TRANS_PREFIX = 'booking_request.spotahome_booking_payment_info';

const PaymentInfo = ({
  pricing,
  downPaymentTimeInterval,
  renderLandlordPolicies = null,
  detailedAdminFee = {},
  areSectionsExpanded = false,
  contractDurationType = 'temporal',
  hideDepositElanLaw = false,
  showDepositDetails = false,
  hasRentCollection = false,
  creationDate,
  isInstantBooking = false
}) => {
  const isDownPaymentWeekly = downPaymentTimeInterval.type === 'weekly';
  const isPermanentContract = contractDurationType === 'permanent';
  const isDownPaymentFirstMonthStrategy =
    pricing.downPaymentStrategy === 'downPaymentFirstMonthStrategy';

  const downPaymentBaseItem = {
    name: getDownPaymentText(pricing, downPaymentTimeInterval),
    price: pricing.downPayment,
    discountType: pricing.offer ? pricing.offer.discountType : null,
    discountValue: pricing.offer ? pricing.offer.discountValue : null,
    priceWithoutOffer: pricing.downPaymentWithoutOffer,
    tooltip: isDownPaymentWeekly
      ? trans(`${SAH_TRANS_PREFIX}.week.in.advance.payment`, {
          weeks: downPaymentTimeInterval.value
        })
      : trans('property_show.book_now_card.tooltip.first_payment.summary'),
    dataTest: isDownPaymentWeekly
      ? 'spotahomeBookingPaymentInfo.downPayment.weeksInAdvance'
      : 'spotahomeBookingPaymentInfo.downPayment'
  };

  const sahFeeItem = {
    show: !!pricing.tenantFeeGross,
    name: trans(`${SAH_TRANS_PREFIX}.summary.our_fee`),
    price: pricing.tenantFeeGross,
    tooltip: trans('property_show.book_now_card.one_time_booking_fee.body'),
    dataTest: 'spotahomeBookingPaymentInfo.fee'
  };
  const promoCodeItem = {
    show: !!pricing.discount,
    name: trans(`${SAH_TRANS_PREFIX}.summary.promo_code`),
    price: -pricing.discount,
    dataTest: 'promo-code'
  };

  const downPaymentItem = isDownPaymentFirstMonthStrategy
    ? {
        ...downPaymentBaseItem,
        ...getDownPaymentForFirstMonthStrategy({
          ...pricing
        })
      }
    : downPaymentBaseItem;

  const getDepositTooltip = () => {
    if (hideDepositElanLaw) {
      return transLL('elan_compliance_deposit_note');
    }
    return (
      (!!pricing.securityDeposit &&
        trans('listing.fees.deposit.button-text')) ||
      (pricing.securityDepositType === 'noSecurityDeposit' &&
        transLL('no-deposit-required'))
    );
  };

  const depositItem = {
    name: transLL('arrival.deposit'),
    price: pricing.securityDeposit,
    hidePrice: hideDepositElanLaw,
    tooltip: !showDepositDetails && getDepositTooltip(),
    dataTest: 'landlordPaymentsTimeline.arrival.deposit'
  };
  const adminFeeItem = {
    show: !!pricing.adminFee,
    name: transLL('arrival.admin_fee'),
    price: pricing.adminFee,
    tooltip: trans('listing.fees.admin-fee.button-text'),
    dataTest: 'landlordPaymentsTimeline.arrival.adminFee'
  };

  const isPermanentContractItem = monthIndex =>
    isPermanentContract && monthIndex === 2;

  const downPaymentIndex = pricing.tenantPayments
    .map(price => +price)
    .findIndex(
      (price, index) =>
        price === 0 &&
        !!pricing.tenantPaymentsWithoutOffer &&
        pricing.tenantPaymentsWithoutOffer[index] === 0
    );

  const getItemDataTest = monthIndex =>
    isPermanentContractItem(monthIndex)
      ? 'landlordPaymentsTimeline.stay.monthRent.restOfMonths'
      : `landlordPaymentsTimeline.stay.monthRent.${monthIndex}`;

  const translateMonthPrefix = monthIndex =>
    isPermanentContractItem(monthIndex)
      ? trans('landlordPaymentsTimeline.stay.restOfMonths')
      : trans(
          'mail.transactional.landlord_recurring_payouts.payout.rent.text',
          {
            month: formatMonthlyPaymentDate(
              addMonths(getDateAsUTC(pricing.checkIn), monthIndex)
            )
          }
        );

  const getTotalToPayPerMonthItems = () => {
    const permanentContractItems = [
      pricing.tenantPayments[0],
      pricing.tenantPayments[1],
      pricing.monthlyPrice * 100
    ];

    return isPermanentContract
      ? permanentContractItems
      : pricing.tenantPayments;
  };

  const monthlyRentItemsBase = getTotalToPayPerMonthItems().map(
    (price, monthIndex) => {
      let resultPrice = +price / 100;
      let alreadyPaid = false;
      let discountType = null;
      let discountValue = null;
      let priceWithoutOffer = null;

      if (monthIndex === downPaymentIndex) {
        resultPrice = pricing.downPayment;
        alreadyPaid = true;
      }

      if (
        monthIndex !== downPaymentIndex &&
        pricing.tenantPaymentsWithoutOffer &&
        pricing.tenantPaymentsWithoutOffer[monthIndex] !== price // use original price without dividing between 100.
      ) {
        discountType = pricing.offer ? pricing.offer.discountType : null;
        discountValue = pricing.offer ? pricing.offer.discountValue : null;
        priceWithoutOffer =
          pricing.tenantPaymentsWithoutOffer[monthIndex] / 100;
      }

      return {
        price: resultPrice,
        alreadyPaid,
        discountType,
        discountValue,
        priceWithoutOffer,
        name: translateMonthPrefix(monthIndex),
        dataTest: getItemDataTest(monthIndex)
      };
    }
  );

  const monthlyRentItems = isDownPaymentFirstMonthStrategy
    ? getPaymentsForFirstMonthStrategy(monthlyRentItemsBase, pricing)
    : monthlyRentItemsBase;

  const reservationPriceWithoutOffer =
    (downPaymentItem.priceWithoutOffer * 100 + sahFeeItem.price * 100) / 100; // fix float point extra decimals.

  const props = {
    pricing,
    hasRentCollection,
    areSectionsExpanded,
    renderLandlordPolicies,
    hideDepositElanLaw,
    detailedAdminFee,
    monthlyRentItems,
    downPaymentItem,
    sahFeeItem,
    promoCodeItem,
    depositItem,
    adminFeeItem,
    reservationPriceWithoutOffer,
    creationDate,
    isInstantBooking
  };

  return <PaymentInfoComponent {...props} />;
};

PaymentInfo.propTypes = {
  pricing: PropTypes.shape({
    currencyIsoCode: PropTypes.string,
    checkIn: PropTypes.string.isRequired,
    checkOut: PropTypes.string.isRequired,
    discount: PropTypes.string,
    adminFee: PropTypes.number,
    monthlyPrice: PropTypes.number,
    downPayment: PropTypes.number,
    downPaymentWithoutOffer: PropTypes.number,
    reservationPrice: PropTypes.number,
    reservationPriceWithoutFormat: PropTypes.number,
    tenantPayments: PropTypes.arrayOf(PropTypes.number || PropTypes.string),
    tenantPaymentsWithoutOffer: PropTypes.arrayOf(
      PropTypes.number || PropTypes.string
    ),
    totalToPayOnMoveIn: PropTypes.number,
    securityDeposit: PropTypes.string,
    securityDepositType: PropTypes.string,
    tenantFeeGross: PropTypes.number,
    offer: PropTypes.shape({
      discountType: PropTypes.string,
      discountValue: PropTypes.number
    }),
    downPaymentStrategy: PropTypes.string,
    tenantPaymentsWithCoverage: PropTypes.shape({
      downPaymentFrom: PropTypes.string,
      downPaymentTo: PropTypes.string,
      downPaymentDays: PropTypes.number,
      payments: PropTypes.arrayOf(
        PropTypes.shape({
          amount: PropTypes.number,
          days: PropTypes.number,
          from: PropTypes.string,
          to: PropTypes.string
        })
      )
    })
  }).isRequired,
  detailedAdminFee: PropTypes.shape({
    totalToPayOnMoveIn: PropTypes.number
  }),
  downPaymentTimeInterval: PropTypes.shape({
    type: PropTypes.oneOf(['months', 'weeks']),
    value: PropTypes.number
  }).isRequired,
  areSectionsExpanded: PropTypes.bool,
  hideDepositElanLaw: PropTypes.bool,
  contractDurationType: PropTypes.string,
  renderLandlordPolicies: PropTypes.func,
  showDepositDetails: PropTypes.bool,
  hasRentCollection: PropTypes.bool,
  creationDate: PropTypes.string.isRequired,
  isInstantBooking: PropTypes.bool
};

export default PaymentInfo;
